import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus, MessageCircleQuestion } from "lucide-react";

// SMRU /RFC specific Q&A.
const SMRU_FAQS = [
  {
    q: "Is this a real, recognized B.Tech degree?",
    a: "Absolutely. You graduate with a full 4-year B.Tech from St. Mary's University (SMRU) — a UGC 2(f) recognized university under Telangana Govt. Act No. 10 of 2026, with a UGC & AICTE-approved degree valid for jobs, higher studies, and government exams.",
  },
  {
    q: "Who is eligible and how do I apply?",
    a: "Students who've completed (or are completing) their 12th / Intermediate with Physics, Chemistry & Mathematics (PCM) are eligible. Just tap “Get Guidance” and fill the 2-minute form — our team will call you, help you choose a specialization, and walk you through enrollment. No entrance-exam stress.",
  },
  {
    q: "What makes the AI.Prof AI-Powered B.Tech different from others?",
    a: "Career prep starts from Semester 1 — not the final year. AI is built into every subject and you start coding in week one with Veda, your 24/7 AI co-pilot. Every course ends in a real project, so you graduate with 40+ verified projects and a Tech Readiness Score companies trust — not just a certificate. Add a guaranteed internship, mentorship from top-company engineers, and a network of 1,800+ hiring companies, and you graduate genuinely industry-ready.",
  },
  {
    q: "Which specializations can I choose?",
    a: "Five future-ready tracks: AI & Machine Learning, Data Science, Cyber Security, Computer Science & Engineering, and Information Technology. You pick your track in Year 1.",
  },
  {
    q: "Is the internship genuinely guaranteed?",
    a: "Yes. Every student is guaranteed at least one internship opportunity during the program through AI.Prof's network of 1,800+ hiring companies — many students intern as early as Year 2.",
  },
  {
    q: "What kind of placements and salaries can I expect?",
    a: "You graduate with a 40+ project portfolio and a verified Tech Readiness Score that companies trust. Our network spans 1,800+ product companies hiring across AI, data, cloud and security — with strong roles for AI/ML, Data Science, Cloud and Cyber Security engineers.",
  },
];

// Rotating brand gradients for colourful per-card accents.
const GRADIENTS = [
  "from-[#0052FF] to-blue-400",
  "from-[#FF6B00] to-orange-400",
  "from-[#0052FF] to-[#FF6B00]",
  "from-[#FF6B00] to-[#0052FF]",
];

function FaqCard({ q, a, gradient, open, onToggle }) {
  return (
    <div
      className={`relative break-inside-avoid mb-5 overflow-hidden rounded-2xl bg-white transition-all duration-300 ${
        open
          ? "shadow-xl ring-1 ring-aiblue/20"
          : "shadow-sm ring-1 ring-gray-100 hover:ring-aiblue/30 hover:shadow-md"
      }`}
    >
      {/* gradient top accent (shows when open) */}
      <span
        aria-hidden="true"
        className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${gradient} transition-opacity duration-300 ${
          open ? "opacity-100" : "opacity-0"
        }`}
      />
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-4 text-left p-5 min-h-[5.5rem]"
      >
        <span
          className={`shrink-0 h-9 w-9 rounded-xl bg-gradient-to-br ${gradient} text-white flex items-center justify-center shadow-sm transition-transform duration-300 ${
            open ? "rotate-90" : ""
          }`}
        >
          {open ? <Minus className="h-5 w-5" /> : <Plus className="h-5 w-5" />}
        </span>
        <span className="flex-1 font-display font-bold text-gray-900 text-[15px] sm:text-base leading-snug">
          {q}
        </span>
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <p className="px-5 pb-5 pl-[4.25rem] text-gray-600 leading-relaxed text-sm sm:text-[15px]">
              {a}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function SmruFaq() {
  const [openIndex, setOpenIndex] = useState(null);
  return (
    <section
      id="faq"
      className="relative overflow-hidden py-20 sm:py-28 bg-gradient-to-b from-white via-aisky/10 to-[#FFF5F0]"
    >
      {/* ambient gradient blobs */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-24 -left-24 w-[26rem] h-[26rem] rounded-full bg-gradient-to-br from-aiblue/15 to-sky-300/20 blur-3xl" />
        <div className="absolute -bottom-24 -right-24 w-[26rem] h-[26rem] rounded-full bg-gradient-to-br from-aiorange/15 to-orange-300/20 blur-3xl" />
      </div>

      <div className="relative max-w-5xl mx-auto px-4 sm:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-2xl mx-auto"
        >
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider text-white shadow-sm bg-gradient-to-r from-aiblue to-aiorange">
            <MessageCircleQuestion className="h-3.5 w-3.5" />
            Got Questions?
          </span>
          <h2 className="mt-5 font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight text-gray-900">
            Everything you need to know about{" "}
            <span className="bg-gradient-to-r from-[#0052FF] to-[#FF6B00] bg-clip-text text-transparent">
              AI.Prof B.Tech Program
            </span>
          </h2>
          <p className="mt-4 text-gray-500">
            Still unsure? Tap “Get Guidance” and our team will answer everything
            personally.
          </p>
        </motion.div>

        {/* Two-column colourful accordion */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mt-12 max-w-3xl mx-auto"
        >
          {SMRU_FAQS.map((item, i) => (
            <FaqCard
              key={item.q}
              q={item.q}
              a={item.a}
              gradient={GRADIENTS[i % GRADIENTS.length]}
              open={openIndex === i}
              onToggle={() => setOpenIndex(openIndex === i ? null : i)}
            />
          ))}
        </motion.div>
      </div>
    </section>
  );
}
